<template>
  <div class="space-y-1">
    <!-- Die Auswahl in drei Gruppen (01.10.): kein Server, Server des Bots,
         und Server, die das System kennt, auf denen der Bot aber NICHT ist
         (Historie, frühere Credentials, gespeicherte Nachrichten). Die
         Option „keiner" trägt den Leerstring als value (ein <option> kann
         kein null tragen); nach außen wird daraus null — ein Leerstring
         ginge als guild_id = '' an den Server, und die Liste wäre still leer. -->
    <div v-if="!handeingabe" class="flex items-center gap-2">
      <BaseSelect
        class="flex-1"
        :modelValue="auswahlWert"
        :groups="gruppen"
        @update:modelValue="waehle"
      />
      <!-- Was „keiner" bedeutet, steht am Icon (30.09.) -- vorher als Satz
           unter JEDER Tabellenzeile ohne Server, und das Select sagt
           „— keiner —" ohnehin schon. fixiert: die Tabelle im Tab klippt
           (overflow-hidden). -->
      <BaseInfoIcon v-if="!modelValue" :text="DISCORD_SERVER.keiner" label="kein Server" fixiert />
    </div>

    <!-- Rückfall ohne Serverliste: die ID von Hand. Nur Ziffern — eine
         Snowflake ist eine Zahl, aber sie bleibt hier eine ZEICHENKETTE
         (über 2^53, Number() verlöre die letzte Stelle). -->
    <div v-else class="flex items-center gap-2">
      <BaseInput
        class="flex-1"
        :modelValue="modelValue || ''"
        inputmode="numeric"
        pattern="[0-9]*"
        placeholder="Server-ID, z. B. 1299857361632317523"
        @update:modelValue="tippe"
      />
      <!-- Die Anleitung (Entwicklermodus → „Server-ID kopieren") am Icon
           statt als Absatz; ohne Server zusätzlich, was das heißt. -->
      <BaseInfoIcon
        :text="modelValue ? DISCORD_SERVER.handeingabe : `${DISCORD_SERVER.handeingabe}\n\n${DISCORD_SERVER.keiner}`"
        label="Server-ID von Hand"
        fixiert
      />
    </div>

    <!-- Der Umschalter zwischen Liste und Handeingabe — sichtbar nur, wenn
         es Einträge gibt. Ohne Einträge ist die Handeingabe der einzige Weg. -->
    <button
      v-if="alle.length"
      type="button"
      class="text-xs text-indigo-600 hover:underline"
      @click="handeingabe = !handeingabe"
    >
      {{ handeingabe ? 'Aus der Liste wählen' : 'ID von Hand eintragen' }}
    </button>

    <p v-if="nameWeichtAb" class="text-xs text-amber-800">{{ DISCORD_SERVER.abweichenderName }}</p>
  </div>
</template>

<script>
import BaseInfoIcon from '@/BaseComponents/BaseInfoIcon.vue';
import BaseInput from '@/BaseComponents/BaseInput.vue';
import BaseSelect from '@/BaseComponents/BaseSelect.vue';
import { DISCORD_SERVER, SERVERWAHL } from '@/async-sessions/discordServer';

/**
 * Die Wahl eines Discord-Servers für ein Micro-Credential — präsentational.
 *
 * EINE Komponente für den Admin-Tab UND den Credential-Dialog, damit die
 * Regeln (Leerstring → null, ID bleibt Zeichenkette, Handeingabe als
 * Rückfall) nicht zweimal daneben liegen. Sie LÄDT NICHTS: beide Listen
 * kommen als Prop, geholt von ladeDiscordServer() und ladeBekannteServer()
 * im Elternteil — sonst fragte jede Tabellenzeile einzeln.
 *
 * ⚠️ Liste oder Handeingabe wird in data() EINMAL entschieden. Kommt eine
 * Liste später an, muss der Elternteil die Komponente per :key neu
 * aufbauen (Falle vom 18.09.: „0 Selects" bei Bot 200).
 *
 * Sie meldet `gewaehlt` mit { id, name }: id null oder Zeichenkette, name
 * null bei Handeingabe (der Name kommt nur aus den Listen).
 */
export default {
  name: 'GuildWahl',
  components: { BaseInfoIcon, BaseInput, BaseSelect },
  props: {
    // Die gespeicherte Guild-ID (Zeichenkette) oder null.
    modelValue: { type: String, default: null },
    // Der gespeicherte Servername (Anzeige-Cache) oder null.
    guildName: { type: String, default: null },
    // [{ id, name }] — die Server des Bots, id als Zeichenkette.
    guilds: { type: Array, default: () => [] },
    // [{ id, name, nachrichten }] — die Server, die das System kennt
    // (GET /admin/bekannte-server). Enthält auch Bot-Server; die zeigt die
    // Gruppe „Bot ist Mitglied", hier fallen sie heraus.
    bekannte: { type: Array, default: () => [] },
    // Schlüssel aus DISCORD_SERVER, wenn die Bot-Liste nicht geladen werden
    // konnte. Erzwingt KEINE Handeingabe mehr (01.10.): die bekannten
    // Server bleiben wählbar.
    botFehler: { type: String, default: '' },
  },
  emits: ['gewaehlt'],
  data() {
    // Alle Einträge = Bot-Server ∪ bekannte Server.
    const alle = [...this.guilds, ...this.bekannte];
    return {
      DISCORD_SERVER,
      // Handeingabe, wenn es KEINE Einträge gibt ODER die gespeicherte ID in
      // keinem steht: sonst zeigte das Select „keiner", während in der
      // Datenbank ein Server steht.
      handeingabe: !alle.length
        || (!!this.modelValue && !alle.some((g) => g.id === this.modelValue)),
    };
  },
  computed: {
    alle() {
      return [...this.guilds, ...this.bekannte];
    },
    // Bekannte Server, auf denen der Bot NICHT ist -- ein Server steht nur
    // in einer Gruppe.
    nurBekannte() {
      const botIds = new Set(this.guilds.map((g) => g.id));
      return this.bekannte.filter((b) => !botIds.has(b.id));
    },
    // Leere Gruppen fallen weg: eine Überschrift ohne Einträge sagt nichts.
    gruppen() {
      return [
        { label: SERVERWAHL.gruppeKeiner, options: [{ value: '', label: '— keiner —' }] },
        {
          label: SERVERWAHL.gruppeBot,
          options: this.guilds.map((g) => ({ value: g.id, label: `${g.name} (${g.id})` })),
        },
        {
          label: SERVERWAHL.gruppeBekannt,
          options: this.nurBekannte.map((b) => ({
            value: b.id,
            label: SERVERWAHL.eintragBekannt(b.name, b.id, b.nachrichten),
          })),
        },
      ].filter((g) => g.options.length);
    },
    auswahlWert() {
      return this.modelValue || '';
    },
    nameWeichtAb() {
      if (!this.modelValue || !this.guildName) return false;
      const live = this.guilds.find((g) => g.id === this.modelValue);
      return !!live && live.name !== this.guildName;
    },
  },
  methods: {
    waehle(wert) {
      // Leer → null, nie ''. Siehe Kommentar im Template.
      if (!wert) {
        this.$emit('gewaehlt', { id: null, name: null });
        return;
      }
      // Der Name zuerst vom Bot (aktuell), sonst aus den bekannten Servern;
      // ein bekannter Server ohne Namen bleibt null.
      const g = this.guilds.find((x) => x.id === wert)
        || this.bekannte.find((x) => x.id === wert);
      this.$emit('gewaehlt', { id: String(wert), name: g && g.name ? g.name : null });
    },
    tippe(wert) {
      const ziffern = String(wert || '').replace(/\D/g, '');
      this.$emit('gewaehlt', { id: ziffern || null, name: null });
    },
  },
};
</script>
